import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PopupService } from '../../services/popup.service';
import { NotificationsService } from 'angular2-notifications';
import { map, mergeMap, Observable, Subject, tap } from 'rxjs';
import { EventsHttpService } from '../../services/events.http.service';
import { EventResponseEvent } from '../../types/event-response.type';
import { startWith } from 'rxjs/operators';
import { ICONS } from '../../constants/constants';
import { FormControl } from '@angular/forms';
import { ROUTES } from '../../constants/routes';

@Component({
  selector: 'app-event-management',
  templateUrl: './event-management.component.html',
  styleUrls: ['./event-management.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventManagementComponent implements OnInit {
  protected readonly ICONS = ICONS;

  @ViewChild('deletePopup', { static: true }) deletePopup: ElementRef;

  get eventCount(): number | string {
    return this.allEvents?.length ?? '...';
  }

  public allEvents: EventResponseEvent[];

  public changesAppliedSubject = new Subject<EventResponseEvent[] | void>();
  public events$: Observable<EventResponseEvent[]> = this.changesAppliedSubject
    .asObservable()
    .pipe(
      startWith((this.route.snapshot.data as any).events),
      mergeMap(() =>
        this.allEvents
          ? new Observable<EventResponseEvent[]>((sub) =>
              sub.next(this.allEvents)
            )
          : this.EventsHttpService.getEvents()
      ),
      map((events) => {
        const searchText = this.searchControl.value?.trim().toLowerCase() ?? '';

        if (searchText.length === 0) {
          return events;
        }

        return events.filter(({ event_name }) =>
          event_name.toLowerCase().includes(searchText)
        );
      }),
      tap((events) => {
        if (!this.allEvents) {
          this.allEvents = events;
        }
      })
    );

  public searchControl = new FormControl('');

  trackByFn(item: EventResponseEvent): number {
    return item.event_id;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private EventsHttpService: EventsHttpService,
    private popupService: PopupService,
    private notifications: NotificationsService
  ) {}

  ngOnInit(): void {
    this.searchControl.valueChanges.subscribe(() => {
      this.changesAppliedSubject.next();
    });
  }

  updateEventsList(id: number): void {
    this.searchControl.setValue('');
    this.allEvents = this.allEvents.filter((e) => e.event_id !== id);
    this.changesAppliedSubject.next();
  }

  editEvent(event: EventResponseEvent): void {
    this.router.navigate([
      `${ROUTES.CMS_EVENT_MANAGEMENT}/modify/${event.event_id}`,
    ]);
  }

  createEvent(): void {
    this.router.navigate([`${ROUTES.CMS_EVENT_MANAGEMENT}/modify`]);
  }

  openDeleteConfirmPopup(event: any): void {
    this.popupService.showPopup(this.deletePopup, { payload: event.event_id });
  }

  deleteEvent(id: number): void {
    this.EventsHttpService.deleteEvent(id)
      .pipe(tap(() => this.updateEventsList(id)))
      .subscribe(() => {
        this.notifications.success('Success!', `Event was deleted`);
      });
  }
}
